import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  UniversalPage,
  UniversalFilterBar,
  PeriodFilter,
  PERIOD_LABELS,
  Card,
  Button,
  NotificationCard,
  StatCard,
  UniversalAsyncBoundary,
  ChartBars,
} from '@afrilink/ui';
import { Upload, Coins, Trophy, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useStaffRole } from '@/shared/hooks/useStaffRole';
import { salesService } from '@/domains/sales/services/salesService';
import { todayScopedKey } from '@/shared/lib/queryClient';
import { useFilterParams, inPeriod } from '@/shared/hooks/useFilterParams';
// formatZARWhole, not the local `zar` this page carried. Byte-identical output
// for every value shown here, and it normalises the narrow no-break space Intl
// emits. See the ~27-copies note on formatZAR in shared/utils/format.ts.
import { formatZARWhole } from '@/shared/utils/format';
import { sastMonthKey, sastRecentMonths } from '@/shared/utils/sastDate';
import { PartnerLinkCard } from '../components/PartnerLinkCard';
import { BankConfirmationLetterCard } from '../components/BankConfirmationLetterCard';
import { DeveloperTagCard } from '@/shared/components/staff/DeveloperTagCard';

/**
 * SalesDashboard — role-dispatch landing page for /sales.
 *
 * Pipeline (lead → quoted → won) + conversion + commission earned. "Won"
 * counts sales_stage='customer' only (lost deals excluded). Commission comes
 * from the RLS-scoped v_sales_agent_commission view (agent sees own; manager
 * sees company; admin all) — it never exposes the company rate (LD-S1).
 */

export default function SalesDashboard() {
  const { user } = useAuth();
  const { staffRole, isAdmin, isManagerOrAdmin } = useStaffRole();
  // Phase 6. Was five useStates plus a hand-rolled Promise.all/.then/.catch.
  // One query now owns the fetch; everything below is DERIVED from it, so there
  // is no state to fall out of sync and no second render pass.
  //
  // Scoped to the SAST day so a tab left open overnight refetches at midnight
  // rather than showing yesterday's pipeline (architect ruling, 2026-08-03), and
  // keyed on the user + role because an agent's view is filtered to their own
  // leads — without that, switching account would serve the previous one's cache.
  const pipelineQ = useQuery({
    queryKey: todayScopedKey('sales', 'dashboard', user?.id ?? 'anon', staffRole ?? 'none'),
    enabled: !!user,
    queryFn: async () => {
      const [leads, converted, commission, quotations] = await Promise.all([
        salesService.listLeads(staffRole === 'sales_agent' ? { assignedAgentId: user!.id } : {}),
        salesService.listConvertedCustomers(),
        salesService.listAgentCommission().catch(() => []),
        salesService.listQuotations().catch(() => []),
      ]);
      return { leads, converted, commission, quotations };
    },
  });

  const loading = pipelineQ.isLoading;
  // Phase 3.3: a failed load must NOT render as "0 open leads / R0 commission" —
  // on a commission-bearing screen an agent cannot tell that from "you have
  // earned nothing". Tiles render "—" while this is set.
  const error = pipelineQ.error
    ? (pipelineQ.error instanceof Error ? pipelineQ.error.message : String(pipelineQ.error))
    : null;

  const leads = pipelineQ.data?.leads ?? [];
  const converted = pipelineQ.data?.converted ?? [];
  const commission = pipelineQ.data?.commission ?? [];
  const quotations = pipelineQ.data?.quotations ?? [];

  // ── Period filter ────────────────────────────────────────────────────────
  // Filtering is CLIENT-SIDE over the single cached result (architect ruling
  // AR-1), on the raw `sale_date` timestamptz rather than the derived
  // `commission_month` — which is correct regardless of how the view buckets.
  // The period deliberately does NOT join the query key (AR-1.2): it is a view
  // concern, and keying on it would fragment one cache entry into six for
  // identical data and refetch on every chip press.
  const filters = useFilterParams({ defaultPeriod: 'this-month' });
  const scoped = filters.period !== 'all-time';

  // FLOW — measured over a window, so the period applies.
  const commissionInPeriod = useMemo(
    () => commission.filter((r) => inPeriod(r.sale_date, filters.range)),
    [commission, filters.range],
  );
  const commissionTotal = commissionInPeriod.reduce(
    (s, r) => s + Number(r.commission_amount || 0),
    0,
  );

  // STOCK — measured right now. Rule S-1: never bound these by a period.
  // "Open leads in June" is not a number; there is only "open leads now".
  const counts = {
    openLeads: leads.filter((l) => l.sales_stage === 'lead').length,
    quoted: leads.filter((l) => l.sales_stage === 'quoted').length,
    unassigned: leads.filter((l) => l.assigned_agent_id === null).length,
    won: converted.filter((c) => c.sales_stage === 'customer').length,
    lost: converted.filter((c) => c.sales_stage === 'lost').length,
  };

  // SM-R3 — expiry is the only time-critical fact on this page.
  const inWindow = (d: number | null, max: number) => d !== null && d >= 0 && d <= max;
  const quoteUrgency = {
    critical: quotations.filter((q) => inWindow(q.days_left, 2)).length,
    week: quotations.filter((q) => inWindow(q.days_left, 7)).length,
    value: quotations
      .filter((q) => inWindow(q.days_left, 7))
      .reduce((s, q) => s + Number(q.amount ?? 0), 0),
  };

  // Mini leaderboard (manager/admin): commission by agent, top 3.
  // Keyed by agent_id, not name: the id is what makes each row a link to
  // /sales/agents/:id, and display_name would collide if two agents shared one.
  const topAgents = (() => {
    const byAgent = new Map<string, { name: string; amount: number }>();
    // FLOW — the leaderboard is the main reason a manager wants a period at all.
    for (const r of commissionInPeriod) {
      if (!r.agent_id) continue;
      const name = r.agent_name || r.agent_email || 'Unknown';
      const prev = byAgent.get(r.agent_id);
      byAgent.set(r.agent_id, { name, amount: (prev?.amount ?? 0) + Number(r.commission_amount || 0) });
    }
    return [...byAgent.entries()]
      .map(([id, v]) => ({ id, name: v.name, amount: v.amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 3);
  })();

  // Commission per SAST month, oldest first, for the tile's sparkline.
  //
  // §4.14: bucketed with sastMonthKey, NOT by slicing the raw timestamptz. A
  // payment at 01:30 SAST on the 1st is 23:30 UTC on the last of the previous
  // month, and the whole point of a trend is that each point sits in the right
  // month. Deliberately over ALL commission, not commissionInPeriod — the
  // sparkline shows where the period sits in the run of months, so filtering it
  // by that same period would flatten it to one point.
  const commissionTrend = useMemo(() => {
    const byMonth = new Map<string, number>();
    for (const r of commission) {
      if (!r.sale_date) continue;
      const key = sastMonthKey(new Date(r.sale_date));
      byMonth.set(key, (byMonth.get(key) ?? 0) + Number(r.commission_amount || 0));
    }
    // sastRecentMonths is newest-first; a trend line reads oldest-first.
    return sastRecentMonths(6)
      .map((m) => ({ month: m.value, amount: byMonth.get(m.value) ?? 0 }))
      .reverse();
  }, [commission]);

  const closed = counts.won + counts.lost;
  const conversionPct = closed > 0 ? Math.round((counts.won / closed) * 100) : null;
  // `funnelMax` is gone with PipelineBar — ChartBars normalises against the
  // largest row itself. It only ever existed because the last bar is the widest.

  const greeting = staffRole === 'sales_agent' ? 'Your queue' : isAdmin ? 'All companies' : 'Your company';

  return (
    <UniversalPage title="Sales Dashboard" description={`${greeting} — ${siteRoleLabel(staffRole)}`}>
      {error && (
        <NotificationCard
          tone="danger"
          title="Could not load your pipeline"
          body={`${error} — the figures below are not reliable. Reload before acting on them.`}
        />
      )}

      {/* The two agent tools, side by side above the numbers.
          Above the pipeline deliberately: for a partner running a campaign the
          link is the tool they came for, and the KPIs only mean anything once
          it has been out. Every agent gets the bank letter, not just managers —
          see that card's own note on why it needs no RLS reasoning.
          Stacked, these two plus the filter bar could push every number below
          the fold on a laptop. Paired, they cost one row.

          FLEX, not grid-cols-2: PartnerLinkCard returns null for staff with no
          campaign token, which is most SightHost-internal staff. A two-column
          grid would hold the empty column open and leave the bank letter at
          half width beside a hole; `[&>*]:flex-1` lets a lone card take the
          whole row. No wrapper divs, or the null child would still occupy one. */}
      <div className="mb-4 flex flex-col gap-4 lg:flex-row [&>*]:min-w-0 [&>*]:flex-1">
        <PartnerLinkCard />
        <BankConfirmationLetterCard />
      </div>

      {/* Developer tag — own row, not squeezed into the flex row above.
          That row's `[&>*]:flex-1` exists so a lone card can take the full
          width when PartnerLinkCard returns null; adding a third child would
          make all three narrow for partners and leave this one
          double-wrapping its button pair on a laptop. */}
      <div className="mb-4">
        <DeveloperTagCard />
      </div>

      {/* Sits directly above the KPI grid, below PartnerLinkCard — that card is
          deliberately first (it is the tool a partner came for) and the bar
          belongs next to what it actually scopes (Rule S-2). */}
      <div className="mb-4">
        <UniversalFilterBar
          activeCount={filters.activeCount}
          onClear={filters.clear}
          period={
            <PeriodFilter
              value={filters.period}
              from={filters.from}
              to={filters.to}
              onChange={filters.setPeriod}
              label="Commission period"
            />
          }
        />
      </div>

      {/* KPI tiles. On error every value renders "—", never 0 — a zero here
          would be indistinguishable from a genuinely empty pipeline. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Open leads"
          value={error ? '—' : counts.openLeads}
          loading={loading}
          interactive={false}
          scope={scoped ? 'now' : undefined}
          sub={isManagerOrAdmin && counts.unassigned > 0 ? `${counts.unassigned} unassigned` : undefined}
          link={{ to: '/sales/leads', label: 'Open leads' }}
        />
        <StatCard
          label="Quoted"
          value={error ? '—' : counts.quoted}
          loading={loading}
          interactive={false}
          scope={scoped ? 'now' : undefined}
          link={{ to: '/sales/quotations', label: 'Open quotations' }}
        />
        {/* STOCK, and it has to be: customer_profile carries created_at,
            assigned_at and lost_at but NO conversion timestamp, so "won in
            period" cannot be derived honestly. Deriving it from commission
            sale_date would silently miss zero-commission conversions (a
            domain-only sale pays a direct agent nothing), which is a wrong
            number that looks right. See the note in the build report. */}
        <StatCard
          label="Customers won"
          value={error ? '—' : counts.won}
          loading={loading}
          interactive={false}
          scope={scoped ? 'now' : undefined}
          tone="success"
          sub={counts.lost > 0 ? `${counts.lost} lost` : undefined}
          link={{ to: '/sales/customers', label: 'Open customers' }}
        />
        {/* FLOW — the tile D1 was about. It used to sum ALL TIME while the page
            it links to defaulted to the current month, so the two disagreed. */}
        <StatCard
          label={staffRole === 'sales_agent' ? 'My commission' : 'Commission earned'}
          value={error ? '—' : formatZARWhole(commissionTotal)}
          loading={loading}
          interactive={false}
          icon={Coins}
          tone="primary"
          sub={PERIOD_LABELS[filters.period]}
          link={{ to: '/sales/commission', label: 'Open commission' }}
          // Shape, not measurement — the value above is the measurement. Six
          // SAST months; suppressed while the figures are unreliable, because a
          // trend line drawn from a failed load is a confident-looking lie.
          sparkline={error ? undefined : commissionTrend.map((m) => m.amount)}
          sparklineLabel={`Commission over the last 6 months: ${commissionTrend
            .map((m) => `${m.month.slice(0, 7)} ${formatZARWhole(m.amount)}`)
            .join(', ')}.`}
        />
      </div>

      {/* SM-R3 — expiring quotations. Renders only when something is actually
          at risk, so it stays a signal rather than furniture. Placed above the
          funnel because it is the only item on this page with a deadline. */}
      {!loading && quoteUrgency.week > 0 && (
        <Link to={`/sales/quotations?within=${quoteUrgency.critical > 0 ? 2 : 7}`} className="mt-6 block">
          <Card
            className={
              quoteUrgency.critical > 0
                ? 'border-danger/40 bg-danger/5 p-5 transition hover:border-danger/70'
                : 'border-warning/40 bg-warning/5 p-5 transition hover:border-warning/70'
            }
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={quoteUrgency.critical > 0 ? 'mt-0.5 size-5 shrink-0 text-danger' : 'mt-0.5 size-5 shrink-0 text-warning'}
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-base font-bold tracking-tight text-foreground">
                  {quoteUrgency.critical > 0
                    ? `${quoteUrgency.critical} quotation${quoteUrgency.critical === 1 ? '' : 's'} lapse${quoteUrgency.critical === 1 ? 's' : ''} within 48 hours`
                    : `${quoteUrgency.week} quotation${quoteUrgency.week === 1 ? '' : 's'} expire${quoteUrgency.week === 1 ? 's' : ''} this week`}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {quoteUrgency.week} expiring in the next 7 days · <span className="font-semibold tabular-nums text-foreground">{formatZARWhole(quoteUrgency.value)}</span> at stake.
                  On expiry the quotation and its order are cancelled automatically.
                </p>
                <span className="mt-2 inline-block text-xs font-semibold text-primary">
                  Open the chase list →
                </span>
              </div>
            </div>
          </Card>
        </Link>
      )}

      {/* Pipeline — NOT a funnel.
          A funnel shape asserts flow-through: that every bar is a subset of the
          one above it. These three do not nest. Live at the time of writing:
          23 open leads, 22 quoted, 30 customers won — the LAST bar is the
          widest, which is why the scale below normalises against max() rather
          than against the top stage. "Won" is a lifetime stock; the other two
          are current stocks.
          A true funnel needs a cohort, and customer_profile carries created_at,
          assigned_at and lost_at but NO conversion timestamp (see the note on
          the "Customers won" tile above). Deriving one from commission
          sale_date would silently drop zero-commission conversions. So this
          stays a comparison of three current quantities, and says so. */}
      <Card className="mt-6 p-6">
        <div className="mb-4 flex items-center justify-between">
          {/* All three bars are stock — current pipeline, not events in a
              window — so the whole card says so rather than each bar. */}
          <h2 className="flex items-center gap-1.5 font-display text-base font-bold tracking-tight text-foreground">
            Pipeline right now
            {scoped && (
              <span className="inline-flex items-center rounded-full border border-border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Now
              </span>
            )}
          </h2>
          <span className="text-xs text-muted-foreground">
            {error
              ? 'Unavailable'
              : conversionPct === null
                ? 'No closed deals yet'
                : `${conversionPct}% win rate · ${counts.won} of ${closed} closed`}
          </span>
        </div>
        {/* The boundary, not bars-at-zero. Empty bars on a failed load read as
            "no pipeline" — the same lie the tiles used to tell with 0. This was
            already handled by a hand-written conditional; it is the shared
            component now so the error, loading and empty cases stay in step
            with every other panel in the product. */}
        <UniversalAsyncBoundary
          isLoading={loading}
          error={error}
          errorTitle="Pipeline unavailable"
          loadingVariant="rows"
          loadingCount={3}
        >
          {/* scale="ordinal": reordering these stages would change the meaning,
              so the order is carried in the colour — one hue, light to dark.
              They were info / warning / success, which are reserved STATUS
              hues. On this same screen amber genuinely means "quotation expiring
              within 7 days" and red means "lapses within 48 hours", so painting
              stage two amber asked the reader to hold two meanings for one
              colour. "Quoted" is not a warning; it is stage two.
              `hint` on the last row because it is the only one of the three
              that is not a current stock. */}
          <ChartBars
            scale="ordinal"
            ariaLabel={`Pipeline: ${counts.openLeads} open leads, ${counts.quoted} quoted, ${counts.won} customers won to date.`}
            data={[
              { label: 'Open leads', value: counts.openLeads },
              { label: 'Quoted', value: counts.quoted },
              { label: 'Customers won', value: counts.won, hint: 'lifetime' },
            ]}
          />
        </UniversalAsyncBoundary>
      </Card>

      {/* Mini leaderboard — manager/admin only.
          The `topAgents.length > 0` guard used to sit in this condition, so the
          whole card vanished when the period held no commission. A manager
          could not tell "nobody earned anything" from "this did not load" —
          the emptiness has to speak. */}
      {isManagerOrAdmin && (
        <Card className="mt-6 p-6">
          <div className="mb-3 flex items-center gap-2">
            <Trophy className="size-4 text-primary" aria-hidden />
            <h2 className="font-display text-base font-bold tracking-tight text-foreground">Top agents by commission</h2>
            {/* FLOW — obeys the period, so name it rather than leave the reader
                guessing which window they are looking at. */}
            <span className="ml-auto text-xs text-muted-foreground">
              {PERIOD_LABELS[filters.period]}
            </span>
          </div>
          {/* Each row links to the agent drill-down. The route and page already
              existed; the rows were simply never made clickable, so the
              "drill down to an agent" path was unreachable from here. */}
          <UniversalAsyncBoundary
            isLoading={loading}
            error={error}
            errorTitle="Could not load commission"
            isEmpty={topAgents.length === 0}
            emptyTitle="No commission in this period"
            emptyDescription="Nothing has been earned in the selected window — try a wider period."
            emptyCompact
            loadingVariant="rows"
            loadingCount={3}
          >
            <ul className="divide-y divide-border">
              {topAgents.map((a, i) => (
                <li key={a.id}>
                  <Link
                    to={`/sales/agents/${a.id}`}
                    className="-mx-2 flex items-center gap-3 rounded-sm px-2 py-2 text-sm transition-colors hover:bg-card-hover"
                  >
                    <span className="w-5 text-center font-semibold text-muted-foreground">{i + 1}</span>
                    <span className="flex-1 truncate text-foreground">{a.name}</span>
                    <span className="font-semibold tabular-nums text-foreground">{formatZARWhole(a.amount)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </UniversalAsyncBoundary>
        </Card>
      )}

      {/* Quick actions */}
      <Card className="mt-6 p-6">
        <h2 className="font-display text-base font-bold tracking-tight text-foreground">Quick actions</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Add customers, work your pipeline, and track commission — all live.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/sales/customers/new"><Button variant="primary">Add customer</Button></Link>
          {staffRole === 'sales_agent' && (
            <Link to="/sales/my-leads"><Button variant="outline">My leads</Button></Link>
          )}
          {isManagerOrAdmin && (
            <>
              <Link to="/sales/leads"><Button variant="outline">All leads</Button></Link>
              <Link to="/sales/import"><Button variant="outline"><Upload className="h-4 w-4" /> Import leads</Button></Link>
            </>
          )}
          <Link to="/sales/commission"><Button variant="outline">Commission</Button></Link>
          {isAdmin && (
            <Link to="/sales/companies"><Button variant="outline">Sales companies</Button></Link>
          )}
        </div>
      </Card>
    </UniversalPage>
  );
}

function siteRoleLabel(role: string | null): string {
  switch (role) {
    case 'sales_agent': return 'Agent';
    case 'sales_manager': return 'Sales manager';
    case 'admin': return 'SightHost admin';
    default: return 'Staff';
  }
}

